import { useQuery } from '@tanstack/react-query';
import * as Api from '../api';

/** IN_PROGRESS: an attempt is open. PURCHASED: paid for, not started yet. Both start without a charge. */
export type TestHolding = 'IN_PROGRESS' | 'PURCHASED';

type Holdings = Awaited<ReturnType<typeof Api.MyHoldings>>['data'];

// A begun test wins over a paid one: it is the one to continue.
const toHoldingByTest = ({ inProgressTestIds, purchasedTestIds }: Holdings) =>
  new Map<string, TestHolding>([
    ...purchasedTestIds.map((id): [string, TestHolding] => [id, 'PURCHASED']),
    ...inProgressTestIds.map((id): [string, TestHolding] => [id, 'IN_PROGRESS']),
  ]);

/**
 * What the learner already holds on the catalog, so a card offers "continue" or
 * "start" instead of asking for the price again. Nothing is kept once the
 * catalog closes (`gcTime: 0`): a test begun, finished or paid for since the
 * last visit changes the action, and a kept answer would show the old one while
 * the new one loads (`isLoading` is false whenever data is cached). A failed
 * load falls back to the plain catalog (the server still refuses a second
 * charge), so it is not retried.
 */
export const useMyTestHoldings = () =>
  useQuery({
    queryKey: ['public-test-holdings'],
    queryFn: async () => (await Api.MyHoldings()).data,
    select: toHoldingByTest,
    gcTime: 0,
    retry: false,
  });

export default useMyTestHoldings;
